import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  Copy,
  Download,
  FileText,
  Key,
  LifeBuoy,
  Loader2,
  Mail,
  Radar,
  Receipt,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLicense } from "@/hooks/useLicense";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { getStripeEnvironment } from "@/lib/stripe";
import { downloadBlueprintPdf } from "@/lib/blueprintPdf";
import { toast } from "sonner";

type LicenseRow = {
  id: string;
  status: string;
  environment: string;
  product_id: string;
  price_id: string;
  license_key: string | null;
  license_key_sent_at: string | null;
  buyer_email: string | null;
  stripe_session_id: string | null;
  stripe_payment_intent_id: string | null;
  created_at: string;
  updated_at: string;
};

type PurchaseRow = {
  id: string;
  trend_id: string;
  amount_cents: number | null;
  created_at: string;
  trends: {
    title: string | null;
    summary: string | null;
    niche: string | null;
    tags: string[] | null;
    total_score: number | null;
    blueprint: unknown;
  } | null;
};

const formatMoney = (cents: number | null) =>
  typeof cents === "number" ? `$${(cents / 100).toFixed(2)}` : "—";

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";


export default function Account() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { hasLicense } = useLicense(user?.id);
  const { openCheckout, checkoutElement } = useStripeCheckout();
  const env = getStripeEnvironment();

  const [rows, setRows] = useState<LicenseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [purchasesLoading, setPurchasesLoading] = useState(true);
  const [refundReason, setRefundReason] = useState("");
  const [refundSending, setRefundSending] = useState(false);
  const [refundSent, setRefundSent] = useState(false);


  useEffect(() => {
    if (!authLoading && !user) navigate("/auth?redirect=/account", { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    supabase
      .from("licenses")
      .select(
        "id,status,environment,product_id,price_id,license_key,license_key_sent_at,buyer_email,stripe_session_id,stripe_payment_intent_id,created_at,updated_at",
      )
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (cancelled) return;
        setRows((data as LicenseRow[]) ?? []);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setPurchasesLoading(true);
    supabase
      .from("blueprint_purchases")
      .select("id,trend_id,amount_cents,created_at,trends(title,summary,niche,tags,total_score,blueprint)")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (cancelled) return;
        setPurchases((data as unknown as PurchaseRow[]) ?? []);
        setPurchasesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const active = rows.find((r) => r.status === "active" && r.environment === env) ?? rows[0] ?? null;

  const copyKey = async () => {
    if (!active?.license_key) return;
    await navigator.clipboard.writeText(active.license_key);
    toast.success("Licence key copied");
  };

  const downloadPurchase = (p: PurchaseRow) => {
    const t = p.trends;
    if (!t?.title) {
      toast.error("This blueprint isn't available to download yet.");
      return;
    }
    downloadBlueprintPdf({
      title: t.title,
      summary: t.summary ?? "",
      niche: t.niche ?? "",
      tags: t.tags ?? [],
      score: t.total_score ?? 0,
      blueprint: (t.blueprint ?? {}) as Record<string, unknown>,
    });
  };

  const sendRefundRequest = async () => {
    if (!user) return;
    if (refundReason.trim().length < 10) {
      toast.error("Please tell us briefly why, so we can process it.");
      return;
    }
    setRefundSending(true);
    const { error } = await supabase.from("support_tickets").insert({
      user_id: user.id,
      email: user.email ?? "",
      subject: "Refund request",
      message:
        `Refund requested from the customer portal.\n\n` +
        `Licence: ${active?.id ?? "none"}\n` +
        `Payment: ${active?.stripe_payment_intent_id ?? "n/a"}\n` +
        `Purchased blueprints: ${purchases.length}\n\n` +
        `Reason: ${refundReason.trim()}`,
    });
    setRefundSending(false);
    if (error) {
      toast.error("Couldn't send your request. Please email info@skaiboss.com.");
      return;
    }
    setRefundSent(true);
    setRefundReason("");
    toast.success("Refund request received — we reply within 24 hours.");
  };

  const buy = () => {
    if (!user) return;
    openCheckout({
      priceId: "trends_discovery_lifetime_39",
      customerEmail: user.email ?? undefined,
      userId: user.id,
      returnUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
    });
  };



  return (
    <>
      <Helmet>
        <title>My Account — Trends Discovery</title>
        <meta
          name="description"
          content="Manage your Trends Discovery lifetime licence, view your payment history and open your dashboard."
        />
        <link rel="canonical" href="https://trendsdiscovery.com/account" />
        <meta name="robots" content="noindex" />
      </Helmet>

      <main className="min-h-screen bg-background text-foreground">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <div className="flex items-center justify-between gap-4 mb-10">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="size-4" /> Back to home
            </Link>
            <span className="inline-flex items-center gap-2 font-mono text-xs text-primary">
              <Radar className="size-4" /> TRENDS DISCOVERY
            </span>
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-bold">My account</h1>
          <p className="mt-2 text-muted-foreground">
            {user?.email ? `Signed in as ${user.email}.` : "Your licence, payments and dashboard access."}
          </p>

          {/* Licence */}
          <section className="mt-10 rounded-2xl border border-border bg-card p-6 md:p-8">
            <div className="font-mono text-xs text-primary mb-2">// LICENCE</div>
            <h2 className="font-display text-2xl font-bold flex items-center gap-2">
              <Key className="size-5 text-primary" /> Lifetime licence
            </h2>

            {loading ? (
              <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Loading your licence…
              </div>
            ) : active ? (
              <div className="mt-6 space-y-5">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="size-5 text-primary" />
                  <span className="font-medium capitalize">{active.status}</span>
                  <span className="text-muted-foreground">· purchased {formatDate(active.created_at)}</span>
                </div>

                <div className="rounded-xl border border-border bg-background/50 p-5">
                  <div className="font-mono text-[11px] text-muted-foreground mb-3">// YOUR KEY</div>
                  <div className="flex flex-wrap items-center gap-3">
                    <code
                      className="font-mono text-lg tracking-widest"
                      data-testid="account-license-key"
                    >
                      {active.license_key
                        ? revealed
                          ? active.license_key
                          : `${active.license_key.slice(0, 6)}•••••••••••`
                        : "Key is being generated…"}
                    </code>
                    {active.license_key && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setRevealed((v) => !v)}>
                          {revealed ? "Hide" : "Reveal"}
                        </Button>
                        <Button variant="outline" size="sm" onClick={copyKey}>
                          <Copy className="size-3.5" /> Copy
                        </Button>
                      </>
                    )}
                  </div>
                  <p className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
                    <Mail className="size-4 shrink-0 mt-0.5" />
                    {active.license_key_sent_at
                      ? `Emailed to ${active.buyer_email ?? "your purchase email"} on ${formatDate(
                          active.license_key_sent_at,
                        )}.`
                      : "We email this key to the address used at checkout right after payment."}
                  </p>
                </div>

                <Button variant="hero" onClick={() => navigate("/dashboard")}>
                  Open my dashboard <ArrowUpRight className="size-4" />
                </Button>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  You don't have a licence yet. One payment of $39 unlocks the full dashboard for life.
                </p>
                <Button variant="hero" onClick={buy}>
                  Get lifetime access — $39
                </Button>
              </div>
            )}
          </section>

          {/* Purchased blueprints */}
          <section className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8">
            <div className="font-mono text-xs text-primary mb-2">// MY BLUEPRINTS</div>
            <h2 className="font-display text-2xl font-bold flex items-center gap-2">
              <FileText className="size-5 text-primary" /> Purchased blueprints
            </h2>
            {purchasesLoading ? (
              <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Loading your blueprints…
              </div>
            ) : purchases.length === 0 ? (
              <div className="mt-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  {hasLicense
                    ? "Your lifetime licence already unlocks every blueprint — open the marketplace to read and download them."
                    : "You haven't bought a single blueprint yet. Buy one on its own, or unlock all of them with the $39 lifetime licence."}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" onClick={() => navigate("/marketplace")}>
                    Browse blueprints
                  </Button>
                  {!hasLicense && (
                    <Button variant="hero" onClick={buy}>
                      Unlock all — $39
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <ul className="mt-6 divide-y divide-border">
                {purchases.map((p) => (
                  <li key={p.id} className="py-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium">{p.trends?.title ?? "Blueprint"}</div>
                      <div className="text-xs text-muted-foreground font-mono mt-1">
                        {formatDate(p.created_at)} · {formatMoney(p.amount_cents)}
                        {p.trends?.niche ? ` · ${p.trends.niche}` : ""}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => downloadPurchase(p)}>
                        <Download className="size-3.5" /> PDF
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/blueprint/${p.trend_id}`)}>
                        View
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Payment history */}

          <section className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8">
            <div className="font-mono text-xs text-primary mb-2">// PAYMENTS</div>
            <h2 className="font-display text-2xl font-bold flex items-center gap-2">
              <Receipt className="size-5 text-primary" /> Payment history
            </h2>
            {loading ? (
              <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Loading…
              </div>
            ) : rows.length === 0 ? (
              <p className="mt-6 text-sm text-muted-foreground">No payments on file yet.</p>
            ) : (
              <ul className="mt-6 divide-y divide-border">
                {rows.map((r) => (
                  <li key={r.id} className="py-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                    <div>
                      <div className="font-medium">Lifetime access — $39 one-time</div>
                      <div className="text-xs text-muted-foreground font-mono mt-1">
                        {formatDate(r.created_at)} · {r.environment}
                        {r.stripe_payment_intent_id ? ` · ${r.stripe_payment_intent_id.slice(0, 18)}…` : ""}
                      </div>
                    </div>
                    <span className="rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-mono text-primary capitalize">
                      {r.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-6 text-xs text-muted-foreground">
              Need a receipt, a refund within 14 days, or a change of email? Write to{" "}
              <a href="mailto:info@skaiboss.com" className="text-primary hover:underline">
                info@skaiboss.com
              </a>{" "}
              or visit the <Link to="/support" className="text-primary hover:underline">support page</Link>.
            </p>
          </section>

          {/* Licence renewal / re-purchase */}
          <section className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8">
            <div className="font-mono text-xs text-primary mb-2">// LICENCE STATUS</div>
            <h2 className="font-display text-2xl font-bold flex items-center gap-2">
              <Key className="size-5 text-primary" /> Renew or restore access
            </h2>
            {hasLicense ? (
              <p className="mt-3 text-sm text-muted-foreground">
                Your $39 licence is a one-time purchase and never expires, so there is nothing to
                renew. If your key ever stops working, use the refund and help form below and we'll
                reissue it.
              </p>
            ) : (
              <div className="mt-3 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Your access is not active right now. A single $39 payment restores the full trend
                  feed, every blueprint and a fresh licence key by email — for life.
                </p>
                <Button variant="hero" onClick={buy}>
                  Restore lifetime access — $39
                </Button>
              </div>
            )}
          </section>

          {/* Refund request */}
          <section className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8">
            <div className="font-mono text-xs text-primary mb-2">// REFUNDS</div>
            <h2 className="font-display text-2xl font-bold flex items-center gap-2">
              <LifeBuoy className="size-5 text-primary" /> Request a refund
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Within 14 days of your payment, ask and we refund it — no conditions. Tell us what
              didn't work and we'll process it back to your original card, usually within 24 hours.
            </p>
            {refundSent ? (
              <p className="mt-6 flex items-center gap-2 text-sm text-primary">
                <CheckCircle2 className="size-4" /> Request received. We'll email you at{" "}
                {user?.email ?? "your account address"}.
              </p>
            ) : (
              <div className="mt-5 space-y-3">
                <Textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows={4}
                  maxLength={1000}
                  placeholder="What went wrong, and which payment should we refund?"
                  aria-label="Refund reason"
                />
                <Button variant="outline" onClick={sendRefundRequest} disabled={refundSending}>
                  {refundSending ? <Loader2 className="size-4 animate-spin" /> : null}
                  Send refund request
                </Button>
              </div>
            )}
          </section>



          {/* Access */}
          <section className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8">
            <div className="font-mono text-xs text-primary mb-2">// ACCESS</div>
            <h2 className="font-display text-2xl font-bold flex items-center gap-2">
              <ShieldCheck className="size-5 text-primary" /> Dashboard access
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              {hasLicense
                ? "Your licence is active, so the live trend feed and full blueprints are unlocked."
                : "The live trend feed and full blueprints unlock as soon as your payment clears."}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => navigate("/dashboard")}>
                Go to dashboard
              </Button>
              <Button variant="ghost" onClick={() => supabase.auth.signOut().then(() => navigate("/"))}>
                Sign out
              </Button>
            </div>
          </section>
        </div>
      </main>
      {checkoutElement}
    </>
  );
}
